/**
 * PaywallNavHeader — compact sticky navigation for PRO paywall surfaces.
 *
 * Pure presentation: never touches access logic, StoreKit, Stripe or roles.
 * - Back: navigate(-1) when a valid previous entry exists, else /dashboard.
 * - Home: always /dashboard.
 * - Optional close "X" when rendered inside a modal.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Home, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

interface PaywallNavHeaderProps {
  /** Renders an extra close button (modal usage). */
  onClose?: () => void;
  /** Render even when the global app header is mounted (modal usage). */
  forceShow?: boolean;
  /** Sticky (default) or static positioning. */
  sticky?: boolean;
  className?: string;
}

export function PaywallNavHeader({ onClose, forceShow = false, sticky = true, className }: PaywallNavHeaderProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [hasAppHeader, setHasAppHeader] = useState(false);

  // Avoid a double header when the global app header is already mounted.
  useEffect(() => {
    setHasAppHeader(!!document.querySelector('[data-app-nav-header="true"]'));
  }, []);

  if (hasAppHeader && !forceShow) return null;

  const isES = language === "es";
  const goHome = () => navigate("/dashboard", { replace: true });
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else goHome();
  };

  return (
    <header
      data-paywall-nav-header="true"
      className={cn(
        "z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "pt-[max(6px,env(safe-area-inset-top))]",
        sticky ? "sticky top-0" : "relative",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={goBack}
            aria-label={isES ? "Atrás" : "Back"}
            className="h-11 w-11"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={goHome}
            aria-label={isES ? "Ir al panel" : "Go to dashboard"}
            className="h-11 w-11"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>

        {onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={isES ? "Cerrar" : "Close"}
            className="h-11 w-11"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>
    </header>
  );
}

export default PaywallNavHeader;
